/**
 * Pure mutation helpers for DemoSong. Every function returns a NEW
 * DemoSong without mutating the input — safe to drop into React state
 * setters (`setSong(prev => mutate(prev, args))`).
 *
 * Live-edit-during-playback is a separate concern: the engine snapshots
 * the song at playDemo() time. To hear edits, stop and play again. We
 * address that in a follow-up step.
 */

import type { DemoRow, DemoSong } from '../audio/demo-song';

function shallowCloneSong(s: DemoSong): DemoSong {
  return {
    ...s,
    patterns: s.patterns.map((pat) => pat.slice()),
    orderList: s.orderList.slice(),
  };
}

/** Replace the pattern at a given order-list slot. */
export function changePatternAtOrder(
  song: DemoSong,
  orderIndex: number,
  patternIndex: number,
): DemoSong {
  if (orderIndex < 0 || orderIndex >= song.orderList.length) {
    throw new Error(`orderIndex ${orderIndex} out of range (0..${song.orderList.length - 1}).`);
  }
  if (patternIndex < 0 || patternIndex >= song.patterns.length) {
    throw new Error(`patternIndex ${patternIndex} out of range (0..${song.patterns.length - 1}).`);
  }
  const next = shallowCloneSong(song);
  next.orderList[orderIndex] = patternIndex;
  return next;
}

/**
 * Append a new order entry pointing to `patternIndex`.
 * Returns the new song and the index of the new entry.
 */
export function addOrderEntry(song: DemoSong, patternIndex: number): DemoSong {
  if (patternIndex < 0 || patternIndex >= song.patterns.length) {
    throw new Error(`patternIndex ${patternIndex} out of range.`);
  }
  const next = shallowCloneSong(song);
  next.orderList.push(patternIndex);
  return next;
}

/**
 * Remove the order entry at `orderIndex`. Refuses to remove the last
 * remaining entry (songs must have at least one). Adjusts loop_point
 * if it pointed at or past the removed entry.
 */
export function removeOrderEntry(song: DemoSong, orderIndex: number): DemoSong {
  if (song.orderList.length <= 1) {
    throw new Error('Cannot remove the last order entry — song must have at least one pattern in the play order.');
  }
  if (orderIndex < 0 || orderIndex >= song.orderList.length) {
    throw new Error(`orderIndex ${orderIndex} out of range.`);
  }
  const next = shallowCloneSong(song);
  next.orderList.splice(orderIndex, 1);
  // Adjust the loop start if it now points past the end or onto/after the removed entry.
  if (next.loopStartOrderIndex !== null) {
    if (next.loopStartOrderIndex === orderIndex) {
      next.loopStartOrderIndex = Math.max(0, orderIndex - 1);
    } else if (next.loopStartOrderIndex > orderIndex) {
      next.loopStartOrderIndex--;
    }
    next.loopStartOrderIndex = Math.min(next.loopStartOrderIndex, next.orderList.length - 1);
  }
  return next;
}

/** Append a new empty pattern to the song. Returns new song; the new pattern's index is `song.patterns.length`. */
export function addPattern(song: DemoSong): DemoSong {
  const empty: DemoRow[] = Array.from({ length: song.rowsPerPattern }, () => ({}));
  const next = shallowCloneSong(song);
  next.patterns.push(empty);
  return next;
}

/** How many patterns exist; useful for "cycle pattern at order" UIs. */
export function patternCount(song: DemoSong): number {
  return song.patterns.length;
}

/** Create a brand-new empty song with one empty pattern and one order entry. */
export function createEmptySong(
  title = 'Untitled',
  rowsPerPattern = 64,
): DemoSong {
  const emptyPattern: DemoRow[] = Array.from({ length: rowsPerPattern }, () => ({}));
  return {
    title,
    rowsPerPattern,
    patterns: [emptyPattern],
    orderList: [0],
    loopStartOrderIndex: 0,
  };
}

/** Cycle the pattern at `orderIndex` by `delta`, wrapping within 0..patternCount-1. */
export function cyclePatternAtOrder(song: DemoSong, orderIndex: number, delta: number): DemoSong {
  const n = patternCount(song);
  const current = song.orderList[orderIndex];
  if (current === undefined) {
    throw new Error(`orderIndex ${orderIndex} out of range.`);
  }
  const next = ((current + delta) % n + n) % n;
  return changePatternAtOrder(song, orderIndex, next);
}
